import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router';
import { visitApi } from 'src/api/visit';
import OrganizationCancelVisitModal from 'src/sections/@dashboard/organization/components/OrganizationCancelVisitModal';
import OrganizationVisitModal from 'src/sections/@dashboard/organization/components/OrganizationVisitModal/OrganizationVisitModal';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import {
  VisitApiVisitControllerCancelVisitByStaffRequest,
  VisitApiVisitControllerRescheduleVisitByStaffRequest,
} from 'woofwoof-api';
import OrganizationUpcomingVisitCard from '../../OrganizationUpcomingVisitCard';
import OrganizationUpcomingVisitCardSkeleton from '../../OrganizationUpcomingVisitCardSkeleton';
import { translateKey } from 'src/locales/translate';


export const OrganizationPatientUpcomingVisitCardContent = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const { isOrganizationAdminRoleSelected, selectedOrganization } =
    useOrganizationContext();
  const [isRescheduleLoading, setIsRescheduleLoading] = useState(false);
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [selectEventId, setSelectEventId] = useState('');
  const [cancelVisitId, setCancelVisitId] = useState('');
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery(
    ['patient-upcoming-visit', patientId, selectedOrganization?.id],
    () =>
      visitApi.visitControllerGetUpcomingVisitsByOrganizationAndProfileId({
        organizationId: selectedOrganization?.id!,
        profileId: patientId!,
      }),
    {
      retry: 1,
      enabled:
        isOrganizationAdminRoleSelected &&
        !!selectedOrganization?.id &&
        !!patientId,
    },
  );

  const { mutateAsync: rescheduleVisitByStaff } = useMutation({
    mutationFn: (
      rescheduleVisitByStaffDto: VisitApiVisitControllerRescheduleVisitByStaffRequest,
    ) =>
      visitApi.visitControllerRescheduleVisitByStaff(rescheduleVisitByStaffDto),
  });

  const { mutateAsync: cancelVisitByStaff } = useMutation({
    mutationFn: (
      cancelVisitByStaffDto: VisitApiVisitControllerCancelVisitByStaffRequest,
    ) => visitApi.visitControllerCancelVisitByStaff(cancelVisitByStaffDto),
  });

  const onCloseVisitModal = useCallback(() => setSelectEventId(''), []);

  const onOpenCancelModal = (visitId?: string) => {
    setCancelVisitId(visitId!);
    setIsCancelModalOpen(true);
  };

  const onCloseCancelModal = useCallback(() => setIsCancelModalOpen(false), []);

  const onReschedule = useCallback(
    async ({
      professionalId,
      date,
      visitId,
    }: {
      professionalId: string;
      date: string;
      visitId: string;
    }) => {
      try {
        setIsRescheduleLoading(true);
        await rescheduleVisitByStaff({
          visitId,
          rescheduleStaffVisitDto: {
            newStartTime: date,
            newProfessionalId: professionalId,
          },
        });
        await refetch();
        onCloseVisitModal();
        enqueueSnackbar(translateKey("snackbar.visitUpdated"), {
          variant: 'success',
        });
      } catch (error) {
        console.error(error);
      } finally {
        setIsRescheduleLoading(false);
      }
    },
    [
      onCloseVisitModal,
      setIsRescheduleLoading,
      rescheduleVisitByStaff,
      enqueueSnackbar,
      refetch,
    ],
  );

  const onCancel = useCallback(async (reason?: string) => {
    try {
      setIsCancelLoading(true);
      await cancelVisitByStaff({
        visitId: cancelVisitId || selectEventId,
        cancelStaffVisitDto: {
          cancellationReason: reason || translateKey("visit.defaultCancelReasonByStaff"),
        },
      });
      await refetch();
      await queryClient.invalidateQueries({
        queryKey: [
          'patient-visit-history',
          selectedOrganization?.id,
          patientId,
        ],
      });
      onCloseCancelModal();
      enqueueSnackbar(translateKey("snackbar.visitCancelled"), {
        variant: 'success',
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsCancelLoading(false);
    }
  }, [
    onCloseCancelModal,
    setIsCancelLoading,
    cancelVisitByStaff,
    enqueueSnackbar,
    refetch,
    cancelVisitId,
    selectEventId,
    queryClient,
    patientId,
    selectedOrganization?.id,
  ]);

  const visits = data?.data;

  if (!isOrganizationAdminRoleSelected) {
    return null;
  }

  if (isLoading) {
    return <OrganizationUpcomingVisitCardSkeleton isPatientContext />;
  }

  if (!visits?.length) return null;

  const nextVisit = visits[0];

  return (
    <>
      <OrganizationUpcomingVisitCard
        visit={nextVisit}
        onEdit={() => setSelectEventId(nextVisit.id)}
        onCancel={onOpenCancelModal}
        onNotesSaved={refetch}
        isPatientContext
      />
      <OrganizationVisitModal
        isOpen={Boolean(selectEventId)}
        onClose={onCloseVisitModal}
        visit={data?.data?.find((visit) => visit.id === selectEventId)}
        onReschedule={onReschedule}
        isRescheduleLoading={isRescheduleLoading}
        editOnly
      />
      <OrganizationCancelVisitModal
        isOpen={isCancelModalOpen}
        onClose={onCloseCancelModal}
        onAccept={onCancel}
        isLoading={isCancelLoading}
      />
    </>
  );
};
